import { forwardRef, useCallback, useMemo } from 'react'
import clsx from 'clsx'

import { ActionFlag, BottomBarType, BuilderPage } from '@makyoapp/makyo-common'
import {
  Alignment,
  ColorVariant,
  CornerRadius,
  DropDownMenu,
  DropdownMenuContentItem,
  MainNavigationBar,
  MaterialIcon,
  NavItem,
  TypographyValue,
  useRtl,
} from '@makyoapp/makyo-tw-ui'

import { parseVariableSelect } from '@/core/app-builder/parser/variable-select'
import { parseVariableTextSingleCell } from '@/core/app-builder/parser/variable-text'
import { parseStringToDescendant } from '@/lib/slate'
import { BuilderNavbarSettings } from '@/adapters/__project__/BuilderNavbarAdapter'
import { useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { PageNavigationArgs, useAppRoutingContext } from '@/context/__project__/AppRoutingContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { checkVisibilityV2 } from '@/hooks/__project__/useCheckVisibilityV2'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { convertToRem, MtTypographyKey } from '@/shared/brand-kit'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { LOGO_PLACEHOLDER } from '@/shared/constants'

const alignmentsMap = {
  leading: 'start',
  center: 'center',
  trailing: 'end',
}

interface BuilderNavBarProps {
  isScrollingDown?: boolean
}

/**
 * BuilderNavBar
 */
export const BuilderNavBar = forwardRef<HTMLDivElement, BuilderNavBarProps>(({ isScrollingDown }, ref) => {
  const { activeBrandKit } = useAppBrandKitContext()
  const { pages, activePage } = useAppPagesContext()
  const dir = useRtl()
  const { navbarSettings, setActiveComponent, setPageAction, setActiveTab } = useAppBuilderContext()
  const { goToPage } = useAppRoutingContext()
  const { dbClient, language, userSpecific } = useDbClientContext()
  const { userEmail, isUserLoggedIn } = useAppAuthContext()
  const { goToPageSafely } = useAppPagesContext()

  const {
    device,
    isPhonePortrait,
    isPhoneLandscape,
    activeBreakpoint,
    isPhone,
    isTablet,
    getMenuWidth,
    getMenuHeight,
    isNavigationBarBottom,
    isNavPositionLeading,
    isNavigationBarTop,
    isSticky,
    getNavbarDisplayOption,
  } = useBreakpoint()

  // access current color scheme (dark/light)
  const [scheme] = useBuilderColorSchemeV2()
  const mode = scheme.current
  const activeLogo = mode === 'dark' ? activeBreakpoint?.logoDark : activeBreakpoint?.logo

  const filterPage = useCallback(
    (item: BuilderPage): boolean => {
      const hasPermissionToSee = checkVisibilityV2(item.visibility, dbClient, userEmail, language, userSpecific)
      const isPagePrivate = item.private
      const canPageVisible = item.visible && hasPermissionToSee
      if (isPagePrivate) {
        return isUserLoggedIn && canPageVisible
      }
      return canPageVisible
    },
    [isUserLoggedIn, dbClient, userEmail, language, userSpecific]
  )

  const getNavigationBarTypographyKey = (device: string): MtTypographyKey => {
    const deviceMap: Record<string, MtTypographyKey> = {
      phone: 'navigationbarPhone',
      'phone-landscape': 'navigationbarPhoneLandscape',
      tablet: 'navigationbarTablet',
      'tablet-landscape': 'navigationbarTabletLandscape',
      desktop: 'navigationbarDesktop',
      tv: 'navigationbarTv',
    }
    return deviceMap[device] || 'navigationbarPhone'
  }

  const labelTypography = useMemo((): TypographyValue | undefined => {
    if (!activeBrandKit?.typography || !device) return undefined

    const typographyKey = getNavigationBarTypographyKey(device)
    const typographyItem = activeBrandKit.typography[typographyKey]

    if (!typographyItem) return undefined

    return {
      typography: `navigationbar-${device}`,
      custom: {
        fontFamily: typographyItem.familyName,
        fontSize: convertToRem(typographyItem.size),
        fontStyle: typographyItem.familyStyle,
        lineHeight: convertToRem(typographyItem.lineHeight),
        letterSpacing: convertToRem(typographyItem.letterSpacing),
        textTransform: typographyItem.textTransform,
      },
    }
  }, [activeBrandKit?.typography, device])

  const generateMenuItems = useCallback(
    (pageList: BuilderPage[], maxItemCount: number): NavItem[] => {
      let items: BuilderPage[] = []
      if (isPhone || isTablet) {
        items = pageList
      } else {
        items = pageList.slice(0, maxItemCount)
      }
      return items.filter(filterPage).map((page) => ({
        id: page.id,
        label: parseVariableTextSingleCell(
          parseStringToDescendant(page.title),
          dbClient,
          language,
          userSpecific,
          userEmail
        ),
        icon: () => <MaterialIcon icon={page.icon} size={24} />,
        badge: parseVariableSelect(page.badgeMetadata, dbClient, userEmail, language, userSpecific, page.tableId)?.[0],
      })) as NavItem[]
    },
    [dbClient, filterPage, isPhone, isTablet, language, userEmail, userSpecific]
  )

  const generateOverflowMenuItems = useCallback(
    (
      pageList: BuilderPage[],
      maxItemCount: number,
      goToPage: (args: PageNavigationArgs) => void
    ): DropdownMenuContentItem[] => {
      return pageList.slice(maxItemCount).map((page) => ({
        label: parseVariableTextSingleCell(
          parseStringToDescendant(page.title),
          dbClient,
          language,
          userSpecific,
          userEmail
        ),
        icon: <MaterialIcon icon={page.icon} size={24} />,
        onClick: () => page?.slug && goToPage({ slug: page.slug }),
      }))
    },
    [dbClient, language, userEmail, userSpecific]
  )

  const defaultLogo = useMemo(() => {
    return activeBrandKit?.logos ? [...activeBrandKit.logos, '#src#', '#add#'] : ['#src#', '#add#']
  }, [activeBrandKit?.logos])

  const navbarSetting = navbarSettings[activeBreakpoint?.viewport as keyof BuilderNavbarSettings]

  const pageList = pages?.filter((p) => {
    return !p.tableId && p.visible && p.enabled
  })

  const getMaxItems = useCallback(
    (device: string) => {
      const navbar = navbarSettings[device as keyof BuilderNavbarSettings]
      return ['desktop', 'tv'].includes(device) ? (navbar?.maxItems ?? 10) : 100
    },
    [navbarSettings]
  )

  const splitMenuByDevice = (pageList: BuilderPage[]) => {
    const devices = [
      { name: 'phone', max: getMaxItems('phone') },
      { name: 'phone-landscape', max: getMaxItems('phone-landscape') },
      { name: 'tablet', max: getMaxItems('tablet') },
      { name: 'tablet-landscape', max: getMaxItems('tablet-landscape') },
      { name: 'desktop', max: getMaxItems('desktop') },
      { name: 'tv', max: getMaxItems('tv') },
    ]
    const result = devices.map(({ name, max }) => {
      const mainData = pageList.slice(0, max)
      const overflowMenus = pageList.slice(max)

      return {
        [name]: mainData,
        overflowMenus: overflowMenus.length > 0 ? overflowMenus : null,
      }
    })
    return result
  }

  const getLogoWidth = (logoWidth: string | undefined) => {
    const width = isPhone ? '55px' : (logoWidth ?? '55px')
    return parseInt(width.replace('px', ''))
  }

  const getShowLogo = (showLogo: boolean | undefined) => {
    const logo = activeBreakpoint?.logo?.value
    if (!showLogo || isPhonePortrait || !logo) return false
    return showLogo
  }

  const pageMenu = splitMenuByDevice(pageList).find((item) => item[device])

  const getMaxItemCount = () => {
    const maxLimit = 10
    if (!pageMenu) return 0
    const itemCount = pageMenu?.[device]?.length ?? 0
    return itemCount < maxLimit ? itemCount : maxLimit
  }

  const getCustomHeight = () => {
    if (!isSticky && isScrollingDown) return '64px'
    return getMenuHeight(activeBreakpoint?.menuHeight)
  }

  const navPositionLeadingClass =
    isNavPositionLeading && dir === 'ltr'
      ? 'left-0 top-0'
      : isNavPositionLeading && dir === 'rtl'
        ? 'right-0 top-0'
        : null

  const getLogo = useMemo(() => {
    return () => {
      if (activeLogo?.value) return activeLogo.value
      if (defaultLogo?.[0] && defaultLogo[0] !== '#src#') return defaultLogo[0]
      return LOGO_PLACEHOLDER
    }
  }, [activeLogo, defaultLogo])

  const isCenterItems = useMemo(() => {
    return device === 'desktop' || device === 'tv'
  }, [device])

  const backgroundColor = useMemo(() => {
    const bgColorValue = mode === 'dark' ? activeBreakpoint?.menuBgColorDark : activeBreakpoint?.menuBgColor

    const defaultValue = mode === 'dark' ? 'primary-90' : 'primary-20'

    if (!bgColorValue || bgColorValue?.includes('--md-sys-color-')) return defaultValue

    if (bgColorValue?.includes('--color-')) {
      return bgColorValue.replace('--color-', '') as ColorVariant
    }

    return bgColorValue as ColorVariant
    //
  }, [activeBreakpoint?.menuBgColor, activeBreakpoint?.menuBgColorDark, mode])

  if (!navbarSetting?.show || activePage?.bbType === BottomBarType.Disabled) return null

  const menuItems = generateMenuItems(pageList, getMaxItemCount())
  if (!menuItems.length) return null

  const overflowMenuItems = generateOverflowMenuItems(pageList, getMaxItemCount(), goToPage)

  const getAlignment = () => alignmentsMap[activeBreakpoint?.menuAlignment as keyof typeof alignmentsMap] as Alignment

  const isShowLogo = getShowLogo(getNavbarDisplayOption('show_logo'))

  return (
    <div
      className={clsx(
        'flex justify-between transition-transform duration-200 z-[40]',
        isNavPositionLeading ? 'h-full' : 'w-screen',
        isScrollingDown && isNavigationBarTop ? `-translate-y-full` : 'translate-y-0',
        (isSticky || isNavPositionLeading) && 'fixed',
        isNavigationBarBottom && 'bottom-0',
        isNavigationBarTop && 'top-0',
        navPositionLeadingClass,
        !isSticky && isNavigationBarTop && 'relative mb-[64px]'
      )}
      style={{
        width: isNavPositionLeading ? `${getMenuWidth(activeBreakpoint?.menuWidth)}` : '100%',
        marginTop: !isSticky && isNavigationBarBottom ? getMenuHeight(activeBreakpoint?.menuHeight) : '0px',
        marginBottom: !isSticky && isNavigationBarTop ? getMenuHeight(activeBreakpoint?.menuHeight) : '0px',
      }}
    >
      <MainNavigationBar
        backgroundColor={backgroundColor}
        navPosition={isPhonePortrait ? 'bottom' : activeBreakpoint?.position}
        orientation={activeBreakpoint?.iconDirection}
        align={getAlignment()}
        showLabels={getNavbarDisplayOption('show_label')}
        showIcons={getNavbarDisplayOption('show_icon')}
        showActiveIndicator={getNavbarDisplayOption('show_active_indicator')}
        showShadow={getNavbarDisplayOption('show_shadow')}
        showHover={getNavbarDisplayOption('show_hover')}
        // spacing={getSpacing()}
        spacing='close'
        width={getMenuWidth(activeBreakpoint?.menuWidth)}
        height={getCustomHeight()}
        items={menuItems}
        defaultActiveItem={activePage?.id}
        activeItem={activePage?.id}
        onActiveItemChange={(id) => {
          const page = pageList?.find((item) => item.id === id)
          setActiveComponent(undefined)
          setActiveTab(LeadingSidebarTab.Page)
          setPageAction({
            flag: ActionFlag.Edit,
            page: page?.id ?? '',
          })
          if (page?.slug) {
            goToPageSafely(page.slug)
          }
          if (page?.slug) goToPageSafely(page.slug)
        }}
        logo={isShowLogo ? getLogo() : undefined}
        logoCornerRadius={activeBreakpoint?.cornerRadius as CornerRadius}
        logoWidth={getLogoWidth(activeBreakpoint?.logoWidth)}
        logoAlign={activeBreakpoint?.logoAlignment as Alignment}
        logoSpacing={16}
        overflowMenu={
          overflowMenuItems.length > 0 &&
          !isPhone &&
          !isTablet && (
            <DropDownMenu
              backgroundColor='primary-20'
              elevation='md'
              borderStyle='solid'
              borderWidth={0.1}
              items={overflowMenuItems}
              triggerElement={<MaterialIcon icon='more_vert' size={24} />}
            />
          )
        }
        centerItems={isCenterItems}
        device={device}
        labelTypography={labelTypography}
        onLogoClick={() => {
          goToPageSafely('/')
        }}
      />
    </div>
  )
})

BuilderNavBar.displayName = 'BuilderBottomBar'
